/**
 * Écran **Grimoire** `/grimoire` — la collection complète (DECISIONS §1.4 A-27, §7.5, F13/F15).
 *
 * Ce que l'écran affiche, et d'où cela vient :
 * - **toutes** les cartes du contenu publié, renvoyées par `GET /me/collection` : le client ne
 *   complète jamais la liste depuis un catalogue local ;
 * - pour une carte **possédée** : nom, rareté, points, description traduite, effets mis en mots et
 *   étiquettes de synergie — tous fournis par le serveur ;
 * - pour une carte **non possédée** : nom, rareté et points, **rien d'autre**. Le serveur renvoie
 *   déjà `descKey`, `effects` et `tags` à `null` (F13) ; la tuile (`ui/CarteGrimoire`) ne peut pas
 *   les rendre non plus, puisqu'ils ne font pas partie de son contrat pour cet état ;
 * - les compteurs de points (`pointsTotal`, `pointsByRarity`) sont **recopiés tels quels** : ce
 *   sont des valeurs serveur (barème de rareté, non dépensables — F15). Le nombre de cartes
 *   possédées est un simple comptage de la liste reçue, jamais une progression calculée.
 *
 * Les **filtres** (élément, rareté, possession) travaillent sur les données publiées : l'élément
 * d'une carte vient du manifeste (`manifest.cards[].element`), qui est public pour toutes les
 * cartes — il ne révèle aucun effet. Un élément absent du manifeste est signalé comme tel, jamais
 * inventé.
 */
import { ELEMENTS } from '@elemora/shared/constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  chargerManifeste,
  lireCollection,
  useSessionStore,
  versErreurApi,
  type ErreurApi,
} from '../api';
import { cleTraductionContenu, formaterDate, formaterNombre } from '../i18n';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { CarteGrimoire, type CarteGrimoireVue } from '../ui/CarteGrimoire';
import { Chargement } from '../ui/Chargement';
import { Entete } from '../ui/Entete';
import { GroupeFiltre, type OptionFiltre } from '../ui/GroupeFiltre';
import { resumeEffet } from '../ui/PanneauCartes';

import type { CarteGrimoire as CarteCollection, CollectionResponse, Manifest, Rarete } from '@elemora/shared/api';

/** Élément publié d'une carte : un des quatre, ou `neutre`. */
type ElementCarte = (typeof ELEMENTS)[number] | 'neutre';

/**
 * Valeurs des filtres. Elles sont typées `string` : la liste fermée est construite par l'écran
 * (boutons), aucune saisie libre ne peut donc introduire une autre valeur. Les bornes réelles
 * sont `grimoire.filtres.*` pour « tous », `ELEMENTS` + `neutre` pour l'élément, `RARETES` pour la
 * rareté et `possedees` pour la possession.
 */

/** Raretés publiées, dans l'ordre du barème. */
const RARETES: readonly Rarete[] = ['commune', 'rare', 'legendaire', 'mythique'];

/**
 * Clés de contenu des libellés d'élément.
 *
 * Écrites en clair plutôt que construites par concaténation : un catalogue de contenu qui
 * renommerait une clé se voit immédiatement ici, et `neutre` (qui n'a pas d'entrée de contenu)
 * pointe explicitement vers le libellé d'interface.
 */
const CLE_ELEMENT: Readonly<Record<ElementCarte, string>> = {
  feu: 'content:element.feu.name',
  eau: 'content:element.eau.name',
  terre: 'content:element.terre.name',
  air: 'content:element.air.name',
  neutre: 'grimoire.filtres.neutre',
};

/** Clés de contenu des libellés de rareté (`content:rarity.<rarete>.name`). */
const CLE_RARETE: Readonly<Record<Rarete, string>> = {
  commune: 'content:rarity.commune.name',
  rare: 'content:rarity.rare.name',
  legendaire: 'content:rarity.legendaire.name',
  mythique: 'content:rarity.mythique.name',
};

/** Données du grimoire : le serveur fait foi pour la collection, le manifeste pour les éléments. */
interface DonneesGrimoire {
  readonly collection: CollectionResponse;
  /** Manifeste publié, `null` si l'appel a échoué : l'écran reste utilisable sans filtre d'élément. */
  readonly manifeste: Manifest | null;
}

export function EcranGrimoire() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useSessionStore((etat) => etat.user);

  const [donnees, setDonnees] = useState<DonneesGrimoire | null>(null);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);
  const [tentative, setTentative] = useState(0);

  const [filtreElement, setFiltreElement] = useState<string>('tous');
  const [filtreRarete, setFiltreRarete] = useState<string>('toutes');
  const [filtrePossession, setFiltrePossession] = useState<string>('toutes');

  /** Chargement partagé : un seul aller-retour même si React exécute l'effet deux fois. */
  const chargementRef = useRef<{ cle: string; promesse: Promise<DonneesGrimoire> } | null>(null);

  useEffect(() => {
    let actif = true;
    const cle = `${tentative}`;
    const existant = chargementRef.current;
    const promesse =
      existant !== null && existant.cle === cle
        ? existant.promesse
        : (async (): Promise<DonneesGrimoire> => {
            // Le manifeste n'est qu'un complément (élément des cartes) : son échec ne prive pas le
            // joueur de son grimoire, il masque seulement le filtre par élément.
            const [collection, manifeste] = await Promise.all([
              lireCollection(),
              chargerManifeste().catch(() => null),
            ]);
            return { collection, manifeste };
          })();
    chargementRef.current = { cle, promesse };

    promesse
      .then((resultat) => {
        if (!actif) return;
        setDonnees(resultat);
        setErreur(null);
      })
      .catch((erreurApi: unknown) => {
        if (!actif) return;
        const normalisee = versErreurApi(erreurApi);
        if (normalisee.statutHttp === 401) return;
        setErreur(normalisee);
      });

    return () => {
      actif = false;
    };
  }, [tentative]);

  const collection = donnees?.collection ?? null;

  /** Élément publié de chaque carte, par identifiant (le manifeste ne révèle aucun effet). */
  const elementsParCarte = useMemo(() => {
    const table = new Map<string, ElementCarte>();
    for (const carte of donnees?.manifeste?.cards ?? []) {
      table.set(carte.id, carte.element);
    }
    return table;
  }, [donnees]);

  /** Éléments réellement présents dans la collection : on ne propose pas un filtre vide. */
  const elementsPresents = useMemo(() => {
    const presents = new Set<ElementCarte>();
    for (const carte of collection?.cards ?? []) {
      const element = elementsParCarte.get(carte.cardId);
      if (element !== undefined) presents.add(element);
    }
    return presents;
  }, [collection, elementsParCarte]);

  /**
   * Libellés de contenu déjà traduits. Déclarés **avant** le premier `return` de JSX : ils
   * s'appuient sur les clés statiques ci-dessus, sans concaténation de clé à l'exécution.
   */
  const libelleElement = (element: ElementCarte): string => t(CLE_ELEMENT[element]);

  const libelleRarete = (rarete: Rarete): string => t(CLE_RARETE[rarete]);

  if (user === null) {
    return (
      <div className="ecran ecran--centree">
        <Entete avecDeconnexion />
        <Chargement />
      </div>
    );
  }

  const optionsElement: OptionFiltre[] = [
    { valeur: 'tous', libelle: t('grimoire.filtres.tousElements') },
    ...ELEMENTS.filter((element) => elementsPresents.has(element)).map((element) => ({
      valeur: element,
      libelle: libelleElement(element),
    })),
    ...(elementsPresents.has('neutre')
      ? [{ valeur: 'neutre', libelle: libelleElement('neutre') }]
      : []),
  ];

  const optionsRarete: OptionFiltre[] = [
    { valeur: 'toutes', libelle: t('grimoire.filtres.toutesRaretes') },
    ...RARETES.map((rarete) => ({ valeur: rarete, libelle: libelleRarete(rarete) })),
  ];

  const optionsPossession: OptionFiltre[] = [
    { valeur: 'toutes', libelle: t('grimoire.filtres.toutesCartes') },
    { valeur: 'possedees', libelle: t('grimoire.filtres.possedeesSeules') },
  ];

  /** Cartes retenues par les trois filtres (aucun filtre = toute la collection). */
  const cartesAffichees = (collection?.cards ?? []).filter((carte) => {
    if (filtrePossession === 'possedees' && !carte.possedee) return false;
    if (filtreRarete !== 'toutes' && carte.rarity !== filtreRarete) return false;
    if (filtreElement !== 'tous') {
      return elementsParCarte.get(carte.cardId) === filtreElement;
    }
    return true;
  });

  /**
   * Construit la vue d'une carte. Les champs masqués ne sont **pas** recopiés pour une carte
   * verrouillée : c'est ce qui garantit qu'aucun effet ne peut atteindre le DOM (F13).
   */
  const versVue = (carte: CarteCollection): CarteGrimoireVue => {
    const element = elementsParCarte.get(carte.cardId) ?? null;
    const commune = {
      cardId: carte.cardId,
      nom: t(cleTraductionContenu(carte.nameKey)),
      rarete: carte.rarity,
      rareteLibelle: libelleRarete(carte.rarity),
      element,
      elementLibelle: element === null ? null : libelleElement(element),
      points: carte.points,
    } as const;

    if (!carte.possedee) return { ...commune, possedee: false };

    return {
      ...commune,
      possedee: true,
      description: carte.descKey === null ? null : t(cleTraductionContenu(carte.descKey)),
      effets: (carte.effects ?? []).map((effet) => resumeEffet(effet, t)),
      tags: carte.tags === null ? [] : [...carte.tags],
      obtenueLe: carte.obtainedAt === null ? null : formaterDate(carte.obtainedAt, i18n.language),
    };
  };

  const nombre = (valeur: number): string => formaterNombre(valeur, i18n.language);
  const possedees = collection?.cards.filter((carte) => carte.possedee).length ?? 0;
  const total = collection?.cards.length ?? 0;
  const aucunFiltreActif =
    filtreElement === 'tous' && filtreRarete === 'toutes' && filtrePossession === 'toutes';

  return (
    <div className="ecran">
      <Entete avecDeconnexion />
      <main className="page-liste grimoire">
        <header className="page-liste__entete">
          <h1 className="page-liste__titre">{t('grimoire.titre')}</h1>
          <p className="page-liste__sousTitre">{t('grimoire.sousTitre')}</p>
        </header>

        {erreur === null ? null : (
          <BandeauErreur
            erreur={erreur}
            surReessai={() => {
              setTentative((valeur) => valeur + 1);
            }}
          />
        )}

        {collection === null ? (
          erreur === null ? (
            <Chargement />
          ) : null
        ) : collection.cards.length === 0 ? (
          <section className="panneau panneau--vide" data-grimoire-vide="oui">
            <h2 className="panneau__titre">{t('grimoire.vide')}</h2>
            <p className="note">{t('grimoire.videAide')}</p>
          </section>
        ) : (
          <>
            <section className="grimoire__compteurs" aria-label={t('grimoire.pointsTotal')}>
              <article className="panneau carte-solde">
                <h2 className="carte-solde__titre">{t('grimoire.pointsTotal')}</h2>
                <p className="carte-solde__valeur" data-points-total={collection.pointsTotal}>
                  {nombre(collection.pointsTotal)}
                </p>
                <p className="carte-solde__aide">{t('grimoire.pointsTotalAide')}</p>
              </article>
              <article className="panneau carte-solde">
                <h2 className="carte-solde__titre">{t('grimoire.possedeesTitre')}</h2>
                <p
                  className="carte-solde__valeur"
                  data-cartes-possedees={possedees}
                  data-cartes-total={total}
                >
                  {t('grimoire.possedeesValeur', {
                    possede: nombre(possedees),
                    total: nombre(total),
                  })}
                </p>
              </article>
            </section>

            <section className="grimoire__raretes" aria-label={t('grimoire.parRarete')}>
              <h2 className="grimoire__sousTitre">{t('grimoire.parRarete')}</h2>
              <ul className="grimoire__liste-rarete">
                {RARETES.map((rarete) => {
                  const points = collection.pointsByRarity[rarete];
                  const possedeesRarete = collection.cards.filter(
                    (carte) => carte.rarity === rarete && carte.possedee,
                  ).length;
                  const totalRarete = collection.cards.filter(
                    (carte) => carte.rarity === rarete,
                  ).length;
                  return (
                    <li
                      className="grimoire__rarete"
                      key={rarete}
                      data-rarete={rarete}
                      data-points-rarete={points}
                    >
                      <span className="grimoire__rarete-nom">{libelleRarete(rarete)}</span>
                      <span className="grimoire__rarete-points">
                        {t('grimoire.points', { count: points })}
                      </span>
                      <span className="grimoire__rarete-cartes">
                        {t('grimoire.possedeesValeur', {
                          possede: nombre(possedeesRarete),
                          total: nombre(totalRarete),
                        })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="grimoire__filtres" aria-label={t('grimoire.filtres.titre')}>
              <h2 className="grimoire__sousTitre">{t('grimoire.filtres.titre')}</h2>
              <div className="grimoire__groupes">
                {elementsPresents.size === 0 ? null : (
                  <GroupeFiltre
                    id="element"
                    legende={t('grimoire.filtres.element')}
                    options={optionsElement}
                    valeur={filtreElement}
                    surChangement={setFiltreElement}
                  />
                )}
                <GroupeFiltre
                  id="rarete"
                  legende={t('grimoire.filtres.rarete')}
                  options={optionsRarete}
                  valeur={filtreRarete}
                  surChangement={setFiltreRarete}
                />
                <GroupeFiltre
                  id="possession"
                  legende={t('grimoire.filtres.possession')}
                  options={optionsPossession}
                  valeur={filtrePossession}
                  surChangement={setFiltrePossession}
                />
              </div>
              <p className="note" data-compteur-filtre={cartesAffichees.length}>
                {t('grimoire.compteur', { count: cartesAffichees.length })}
              </p>
            </section>

            {cartesAffichees.length === 0 ? (
              <section className="panneau panneau--vide" data-grimoire-filtre-vide="oui">
                <h2 className="panneau__titre">{t('grimoire.videFiltre')}</h2>
                <p className="note">{t('grimoire.videFiltreAide')}</p>
                <Bouton
                  variante="secondaire"
                  onClick={() => {
                    setFiltreElement('tous');
                    setFiltreRarete('toutes');
                    setFiltrePossession('toutes');
                  }}
                >
                  {t('grimoire.filtres.reinitialiser')}
                </Bouton>
              </section>
            ) : (
              <ul className="grimoire__cartes" aria-label={t('grimoire.liste')}>
                {cartesAffichees.map((carte) => (
                  <CarteGrimoire key={carte.cardId} carte={versVue(carte)} />
                ))}
              </ul>
            )}

            {aucunFiltreActif ? null : (
              <Bouton
                variante="discret"
                onClick={() => {
                  setFiltreElement('tous');
                  setFiltreRarete('toutes');
                  setFiltrePossession('toutes');
                }}
              >
                {t('grimoire.filtres.reinitialiser')}
              </Bouton>
            )}
          </>
        )}

        <nav className="page-liste__pied" aria-label={t('commun.navigation')}>
          <Bouton variante="secondaire" onClick={() => navigate('/')}>
            {t('commun.retourAccueil')}
          </Bouton>
        </nav>
      </main>
    </div>
  );
}
